<!--
// app/components/pages/region-world-cup/KwcTablesAllLeagues.vue
-->
<script setup lang="ts">
import TableKwccProbability from "~/app/components/pages/region-tables-kwcc/TableKwccProbability.vue";
import { arrowsSliderVisibility } from "~/src/Shared/utils";
import SliderArrowsBehaviour from "~/app/components/Shared/slider-matches/SliderArrowsBehaviour.vue";

type Props = {
  isHome?: boolean;
};

const props = defineProps<Props>();

const localePath = useLocalePath();

const { data, error } = await useFetch("/api/cms/tables-kwcc-leagues");
if (error.value) console.error(error.value);

const numberOfLeagues: number = 5;

onMounted(() => {
  const tablesContainer: HTMLElement | null = document.getElementById("tablesSlider");
  const bracketButtonLeft: HTMLElement | null = document.getElementById("bracketButtonLeft");
  const bracketButtonRight: HTMLElement | null = document.getElementById("bracketButtonRight");

  if (tablesContainer) {
    let childrenTablesContainerWidth: number = 0;
    const childrenTablesContainer = [...tablesContainer.children];
    childrenTablesContainer.forEach(item => (childrenTablesContainerWidth += item.clientWidth));

    tablesContainer.addEventListener("scroll", () => {
      arrowsSliderVisibility("general", tablesContainer, bracketButtonLeft, bracketButtonRight);
    });
  }
});
</script>

<template>
  <section v-if="data" class="kql-w-container py-6 xl:px-0 grid grid-cols-1">
    <div :class="['mt-8 md:p-4 rounded-lg lg:mt-0', { 'md:bg-gray-900': isHome }]">
      <div class="px-4 md:px-0">
        <div v-if="isHome" class="flex text-gray-400 uppercase text-sm mb-2 justify-between items-end">
          <p>Road to KWC Clubs 2025</p>
          <nuxt-link
            :to="
              localePath({
                name: `region-Tables`,
                params: { region: 'world-cup' },
              })
            "
            class="normal-case text-primary text-xs"
          >
            {{ $t("cards.stats.seeMore.link") }}
          </nuxt-link>
        </div>
        <hr v-if="isHome" />
        <slider-arrows-behaviour
          button-right-id="bracketButtonRight"
          button-left-id="bracketButtonLeft"
          :button-scroll-distance="204"
          slider-container-id="tablesSlider"
          :conditionally-render-if="!isHome"
        >
          <div id="tablesSlider" class="max-w-full overflow-x-scroll hide-scrollbar">
            <div
              :class="[
                'grid grid-flow-col gap-x-2 min-w-max',
                isHome ? 'md:gap-x-0 mt-4 justify-stretch' : ' md:gap-x-4',
              ]"
            >
              <div v-if="data.spain && numberOfLeagues >= 1">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Spain</component
                >
                <table-kwcc-probability
                  :last-updated="data.spain.lastUpdate"
                  second-col-header="pts s5"
                  :teams-obj="data.spain.teams"
                  :table-data="data.spain.data"
                  :first-col-header="isHome ? 'pts' : 'pts s4'"
                  :show-second-and-total-col="!isHome"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                ></table-kwcc-probability>
              </div>
              <div v-if="data.americas && numberOfLeagues >= 2">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Openbank Americas</component
                >
                <table-kwcc-probability
                  :last-updated="data.americas.lastUpdate"
                  second-col-header="pts s3"
                  :teams-obj="data.americas.teams"
                  :table-data="data.americas.data"
                  :first-col-header="isHome ? 'pts' : 'pts s2'"
                  :show-second-and-total-col="!isHome"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                ></table-kwcc-probability>
              </div>
              <div v-if="data.italy && numberOfLeagues >= 3">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Lottomatica.sport Italy</component
                >
                <table-kwcc-probability
                  :last-updated="data.italy.lastUpdate"
                  second-col-header="pts s1"
                  :teams-obj="data.italy.teams"
                  :table-data="data.italy.data"
                  :first-col-header="isHome ? 'pts' : 'pts s1'"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                ></table-kwcc-probability>
              </div>
              <div v-if="data.brazil && numberOfLeagues >= 4">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League Brazil</component
                >
                <table-kwcc-probability
                  :last-updated="data.brazil.lastUpdate"
                  second-col-header="pts s1"
                  :teams-obj="data.brazil.teams"
                  :table-data="data.brazil.data"
                  :first-col-header="isHome ? 'pts' : 'pts s1'"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                ></table-kwcc-probability>
              </div>
              <div v-if="data.france && numberOfLeagues >= 5">
                <component
                  :is="isHome ? 'p' : 'h2'"
                  :class="[
                    'font-bold max-w-full whitespace-pre px-2',
                    { 'text-lg md:text-xl text-left md:text-center': !isHome },
                  ]"
                  >Kings League France</component
                >
                <table-kwcc-probability
                  :last-updated="data.france.lastUpdate"
                  second-col-header="pts s1"
                  :teams-obj="data.france.teams"
                  :table-data="data.france.data"
                  :first-col-header="isHome ? 'pts' : 'pts s1'"
                  :is-home-table="isHome"
                  :is-in-world-cup-tables="!isHome"
                ></table-kwcc-probability>
              </div>
            </div>
          </div>
        </slider-arrows-behaviour>

        <div v-if="!isHome" class="flex flex-wrap gap-x-8 gap-y-4 justify-end">
          <div class="flex items-center gap-x-3">
            <div class="w-1.5 h-6 !bg-red-500"></div>
            <div class="text-xl !text-red-500">
              {{ $t(`tables.legend.placement.qualified`) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
